import { PlusOutlined, MinusOutlined } from '@ant-design/icons';
import { parseJson } from '@medicusunion/front-utils';
import {
  App,
  Button,
  Col,
  DatePicker,
  Flex,
  Form,
  Input,
  Row,
  Select,
  Space
} from 'antd';
import { GENDERS } from 'constants/users';
import { FC, memo, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { fetchAdminLanguages } from 'services/api/translatorPlans';
import { AdminLanguage } from 'types/api/translatorPlans';
import dayjs from 'dayjs';
import { usePermission } from 'hooks';
import { PERMISSION_ROUTES, PERMISSION_ACTIONS } from 'constants/permissions';
import { fetchSpecialties } from 'services/api/specialties';
import { SpecialtyListItem } from 'types/api/specialties';
import { RTL_LANGUAGES } from 'constants/translations';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { attachDoctorToServices, updateDoctor } from 'services/api/doctors';

import type { ProfileForm, LanguageType } from '../../_types';
import { useDoctorProfile } from 'pages/DoctorProfile/DoctorProfileContext';
import { pickLocale } from 'utils/pickLocale';
import { showErrorMessage } from 'utils/showErrorMessage';
import { OrganizationResult, Private } from 'components/shared';
import { LanguageRow } from './styled';
import useDoctorProfileCache from 'hooks/useDoctorProfileCache';
import { ServicesSelect } from 'pages/DoctorProfile/components';

dayjs.extend(utc);
dayjs.extend(timezone);

interface IProps {
  id: string;
  lang: string;
  allowToEdit?: boolean;
}

const PersonalInformation: FC<IProps> = ({ id, lang, allowToEdit }) => {
  const { t } = useTranslation();
  const { checkUserPermission } = usePermission();
  const [form] = Form.useForm();
  const { message } = App.useApp();
  const { profile } = useDoctorProfileCache({ id, lang });
  const { refresh } = useDoctorProfile();
  const [isSaving, setIsSaving] = useState(false);

  const [specialtiesData, setSpecialtiesData] = useState<SpecialtyListItem[]>(
    []
  );

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        const res = await fetchSpecialties({ lang });
        if (!cancelled) setSpecialtiesData(res);
      } catch {
        // swallow — empty options on failure
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [lang]);

  const [languagesData, setLanguagesData] = useState<AdminLanguage[]>([]);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        const rows = await fetchAdminLanguages(200);
        if (!cancelled) setLanguagesData(rows);
      } catch {
        // swallow — empty picker on failure
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, []);

  const onFinish = async (values: ProfileForm) => {
    const doctorId = id || profile?.id;
    if (!doctorId) return;

    const services = values.services;
    delete values.email;
    delete values.services;

    const languages = (values.languages as LanguageType[])
      ?.map((item, index) => {
        return {
          ...item,
          default: index === 0
        };
      })
      .filter(item => item.value);

    const experienceStartDate = dayjs
      .tz()
      .utc()
      .year(
        values.experience_start_date
          ? +dayjs(values.experience_start_date).format('YYYY')
          : dayjs().year()
      )
      .month(0)
      .date(1)
      .hour(0)
      .minute(0)
      .second(0)
      .millisecond(0)
      .format();

    try {
      setIsSaving(true);
      await updateDoctor(doctorId, {
        ...values,
        experience_start_date: experienceStartDate,
        professional_title: values.professional_title || '',
        specialties: values?.specialties?.map(item => +item) || [],
        languages: languages?.length ? languages : null,
        lang
      });
      await attachDoctorToServices({
        doctor_id: Number(doctorId),
        service_ids: (services || []).map(item => Number(item))
      });
      message.success('Successfully saved');
      refresh();
    } catch (err) {
      showErrorMessage(err);
    } finally {
      setIsSaving(false);
    }
  };

  const hasProfileUpdatePermission = checkUserPermission(
    PERMISSION_ROUTES.doctors,
    PERMISSION_ACTIONS.update
  );

  const languages = parseJson(profile?.languages);

  return (
    <OrganizationResult>
      <Form
        form={form}
        layout="vertical"
        size="large"
        disabled={!hasProfileUpdatePermission}
        initialValues={{
          first_name: pickLocale(profile?.first_name, lang),
          last_name: pickLocale(profile?.last_name, lang),
          email: profile?.email || '',
          languages: languages?.length ? languages : [{}],
          gender: profile?.gender || '',
          phone: profile?.phone || '',
          address: profile?.address || '',
          professional_title: pickLocale(profile?.professional_title, lang),
          experience_start_date: profile?.experience_start_date,
          timezone: profile?.timezone || '',
          specialties: profile?.specialties?.map(item => item.id) || [],
          services: profile?.service?.map(item => item.id) || []
        }}
        onFinish={onFinish}
      >
        <Row gutter={[16, 0]}>
          <Col md={12}>
            <Form.Item
              name="first_name"
              label={t('doctor_profile.name')}
              rules={[{ required: true }]}
            >
              <Input
                dir={RTL_LANGUAGES.includes(lang) ? 'rtl' : 'ltr'}
                maxLength={255}
                disabled={!allowToEdit}
              />
            </Form.Item>
          </Col>
          <Col md={12}>
            <Form.Item
              name="last_name"
              label={t('doctor_profile.surname')}
              rules={[{ required: true }]}
            >
              <Input
                dir={RTL_LANGUAGES.includes(lang) ? 'rtl' : 'ltr'}
                maxLength={255}
                disabled={!allowToEdit}
              />
            </Form.Item>
          </Col>
          <Col md={24}>
            <Form.Item name="professional_title" label="Professional Title">
              <Input
                dir={RTL_LANGUAGES.includes(lang) ? 'rtl' : 'ltr'}
                maxLength={255}
                disabled={!allowToEdit}
              />
            </Form.Item>
          </Col>
          <Col md={12}>
            <Form.Item
              name="gender"
              label={t('doctor_profile.gender')}
              rules={[{ required: true }]}
            >
              <Select
                direction={RTL_LANGUAGES.includes(lang) ? 'rtl' : 'ltr'}
                options={GENDERS}
                disabled={!allowToEdit}
              />
            </Form.Item>
          </Col>
          <Col md={12}>
            <Form.Item
              name="timezone"
              label={t('doctor_profile.timezone')}
              rules={[{ required: true }]}
            >
              <Select
                direction={RTL_LANGUAGES.includes(lang) ? 'rtl' : 'ltr'}
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                options={Intl.supportedValuesOf('timeZone').map(item => ({
                  label: item,
                  value: item
                }))}
                size="large"
                allowClear
                showSearch
                filterOption={(input, option) =>
                  (option?.label as unknown as string)
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
                disabled={!allowToEdit}
              />
            </Form.Item>
          </Col>
          <Col md={12}>
            <Form.Item
              name="phone"
              label={t('doctor_profile.phone')}
              rules={[{ required: true }]}
            >
              <Input
                dir={RTL_LANGUAGES.includes(lang) ? 'rtl' : 'ltr'}
                maxLength={255}
                disabled={!allowToEdit}
              />
            </Form.Item>
          </Col>
          <Col md={12}>
            <Form.Item name="email" label={t('doctor_profile.email')}>
              <Input
                dir={RTL_LANGUAGES.includes(lang) ? 'rtl' : 'ltr'}
                maxLength={255}
                disabled
              />
            </Form.Item>
          </Col>
          <Col md={12}>
            <Form.Item
              name="address"
              label={t('doctor_profile.location')}
              rules={[{ required: true }]}
            >
              <Input
                dir={RTL_LANGUAGES.includes(lang) ? 'rtl' : 'ltr'}
                maxLength={255}
                disabled={!allowToEdit}
              />
            </Form.Item>
          </Col>
          <Col md={12}>
            <Form.Item
              name="experience_start_date"
              label="Experience Start Date"
              getValueProps={value => {
                return {
                  value: value ? dayjs(value) : undefined
                };
              }}
            >
              <DatePicker
                picker="year"
                style={{ width: '100%' }}
                disabled={!allowToEdit}
                disabledDate={current =>
                  current.year() < dayjs().year() - 70 ||
                  current.year() > dayjs().year()
                }
              />
            </Form.Item>
          </Col>
          <Col md={24}>
            <Form.Item
              label="Specialties"
              name="specialties"
              rules={[{ required: true }]}
            >
              <Select
                direction={RTL_LANGUAGES.includes(lang) ? 'rtl' : 'ltr'}
                optionFilterProp="children"
                filterOption={(input, option) =>
                  option?.label.includes(input) || false
                }
                filterSort={(optionA, optionB) =>
                  optionA.label
                    .toLowerCase()
                    .localeCompare(optionB?.label.toLowerCase())
                }
                options={specialtiesData.map(item => ({
                  label: item.name,
                  value: item.id
                }))}
                mode="multiple"
                disabled={!allowToEdit}
              />
            </Form.Item>
          </Col>
          <Col md={24}>
            <ServicesSelect lang={lang} disabled={!allowToEdit} />
          </Col>
          <Col md={24}>
            <Form.List name="languages">
              {(fields, { add, remove }) => (
                <>
                  {fields.map((field, index) => (
                    <LanguageRow key={`language-${field.key}`}>
                      <Space size={4} style={{ width: '100%' }} align="end">
                        <Form.Item
                          name={field.name}
                          label={index === 0 && t('doctor_profile.languages')}
                          style={{ margin: 0 }}
                          rules={[{ required: true }]}
                        >
                          <Select
                            direction={
                              RTL_LANGUAGES.includes(lang) ? 'rtl' : 'ltr'
                            }
                            options={languagesData.map(item => ({
                              value: item.code,
                              label: item.name
                            }))}
                            placeholder={t('profile.select_language')}
                            optionFilterProp="label"
                            labelInValue
                            allowClear
                            showSearch
                            filterOption={(input, option) =>
                              (option?.label as unknown as string)
                                .toLowerCase()
                                .includes(input.toLowerCase())
                            }
                            disabled={!allowToEdit}
                          />
                        </Form.Item>
                        <Button onClick={add} disabled={!allowToEdit}>
                          <PlusOutlined />
                        </Button>
                        {index > 0 && (
                          <Button
                            onClick={() => remove(index)}
                            disabled={!allowToEdit}
                          >
                            <MinusOutlined />
                          </Button>
                        )}
                      </Space>
                    </LanguageRow>
                  ))}
                </>
              )}
            </Form.List>
          </Col>
          <Col span={24}>
            <Flex justify="end" gap={12}>
              <Private
                route={PERMISSION_ROUTES.doctors}
                action={PERMISSION_ACTIONS.update}
              >
                <Button
                  size="large"
                  type="primary"
                  onClick={form.submit}
                  loading={isSaving}
                  disabled={!allowToEdit}
                >
                  Save
                </Button>
              </Private>
            </Flex>
          </Col>
        </Row>
      </Form>
    </OrganizationResult>
  );
};

export default memo(PersonalInformation);
